'use client';

import { useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { Box, CircularProgress, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import { useAuthStore } from '@/stores/authStore';
import { useConfigStore } from '@/stores/configStore';
import {
  getCurrentReturnUrl,
  initiateAnonymousSignIn,
  resolveSignInContext,
  type SignInContext,
} from '@/lib/auth/signInFlow';
import { log } from '@/lib/utils/logger';

type AnonymousSignInGateProps = {
  /** Where to return after sign-in; defaults to the current URL. */
  returnUrl?: string;
  /** Override title/message; otherwise derived from returnUrl. */
  context?: SignInContext;
};

/**
 * Shows why sign-in is required, then runs the standard anonymous sign-in flow
 * (direct MSAL redirect or /sign-in chooser).
 */
export function AnonymousSignInGate({ returnUrl, context: explicitContext }: AnonymousSignInGateProps) {
  const router = useRouter();
  const { loginRedirect, isInProgress } = useAuthStore();
  const { systemConfig } = useConfigStore();
  const startedRef = useRef(false);

  const target = returnUrl ?? getCurrentReturnUrl();
  const { title: contextTitle, message: contextMessage } = resolveSignInContext(
    target,
    systemConfig?.basePath ?? '',
    explicitContext
  );
  const choosingProvider = Boolean(systemConfig?.enableShibboleth);

  useEffect(() => {
    if (startedRef.current || isInProgress) {
      return;
    }
    startedRef.current = true;

    void initiateAnonymousSignIn(systemConfig, { loginRedirect }, target, router, {
      title: contextTitle,
      message: contextMessage,
    }).catch((error) => {
      log.error('[AnonymousSignInGate] Sign-in redirect failed:', error);
      startedRef.current = false;
    });
  }, [target, contextTitle, contextMessage, systemConfig, loginRedirect, router, isInProgress]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 64px)',
        gap: 2,
        p: 3,
      }}
    >
      <LockIcon color="primary" sx={{ fontSize: 64 }} />
      <Typography variant="h4" component="h1" gutterBottom textAlign="center">
        {contextTitle}
      </Typography>
      <Typography variant="body1" color="text.secondary" align="center" sx={{ maxWidth: 560 }}>
        {contextMessage}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary">
          {choosingProvider ? 'Taking you to sign in…' : 'Redirecting to sign in…'}
        </Typography>
      </Box>
    </Box>
  );
}
